import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';

import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

import { MediaFile, User } from '@app/models';
import { AuthService, FileService, MaterialCategoryService, MaterialService, SharedUtilsService } from '@app/services';

@Component({
  selector: 'edit-material',
  templateUrl: './edit-material.component.html',
  styleUrl: './edit-material.component.scss',
  standalone: false
})
export class EditMaterialComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  @ViewChild('file') file;
  @ViewChild('ownCoverImage') ownCoverImage;

  public files: Set<File> = new Set();
  public files2: Set<File> = new Set();

  currentUser: User = null;
  materialBeingUpdated: MediaFile;
  currentMaterialId: string = null;
  currentlySavedImage: string = null;
  tempMainFileName: string = null;
  fileToPreview: string | null = null;
  descriptionLimit: number = 100;
  currentWordCount: number = 0;
  wordsLeft: number = 100; // Should be the same as descriptionLimit.
  defaultImageNumber: number = 1;
  selectedCount: number = 0;
  hasError: any = null;
  subCategorySelect = new FormControl();

  materialCategories: any[] = [];
  materialStore: any[] = [];
  currentCategories: any[] = [];
  currentSubCategories: any[] = [];
  imagesToUpload: any[] = [];
  coverImages: string[] = [];
  originalCoverImages: File[] = [];
  filetypesToNotInclude: string[] = [];

  showLoader: boolean = true;
  validForm: boolean = false;
  changeContent: boolean = false;
  stopRepeatFireOfEvent: boolean = false;
  maxLimitReached: boolean = false;
  categoryMissing: boolean = false;
  subCategoryMissing: boolean = false;

  // Editor stuff. Not used right now.
  editorContent: any;
  uploadValue: string = '1';
  clearedCkEditorContents: boolean = false;
  errorMsg: string = '';

  // For Tos. Not used right now.
  checked: boolean = false;
  indeterminate: boolean = false;
  labelPosition: string = 'after';

  updatedMaterialInfo: any = {
    title: '',
    category: '',
    subCategory: '',
    categories: [],
    description: '',
    defaultImage: '',
    pdfPath: '',
    videoPath: '',
    fileType: '',
    fileImageArray: []
  };

  constructor(
    private route: ActivatedRoute,
    private _location: Location,
    private _authService: AuthService,
    private _fileService: FileService,
    private _materialCategoryService: MaterialCategoryService,
    private _materialService: MaterialService,
    public _sharedService: SharedUtilsService,
    public _tosDialog: MatDialog
  ) {
    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
      this.currentMaterialId = params?.materialId ?? null;
    });

    this.filetypesToNotInclude = this._sharedService?.nonSupportedFiles ?? [];
  }


  ngOnInit() {
    this.initializeEditData();
  }


  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }


  async initializeEditData() {
    this.showLoader = true;
    this.currentUser = this._authService.getCurrentlyLoggedInUser();

    // If no current user, go back and return early.
    if (!this.currentUser?._id) {
      this._sharedService.showErrorDialog('You must be logged in to edit this material.', 6000);
      this.goBack();
      return;
    }

    this.materialBeingUpdated = await this.getMaterialToEdit();
    // console.log('Material Being Edited: ', this.materialBeingUpdated);

    // If no material was found, go back and return early.
    if (!this.materialBeingUpdated?._id) {
      this._sharedService.showErrorDialog('Material not found', 6000);
      this.goBack();
      return;
    }

    const materialUserId = this.materialBeingUpdated?.user?._id ?? this.materialBeingUpdated?.user ?? null;

    // Check admin access.
    const hasAdminAccess = this._authService.checkForAdminPrivileges(this.currentUser); // Check admin access.
    const hasOwnerAccess = (materialUserId && this.currentUser._id === materialUserId); // Check owner access.

    // Proceed if they have access. Otherwise, go back.
    if (hasAdminAccess || hasOwnerAccess) {
      await this.getMaterialCategories(); // Get all categories.
      this.initializeMaterial(); // Populate material data.
    } else {
      this._sharedService.showErrorDialog('You do not have access to edit this material. Edit Cancelled', 6000);
      this.goBack();
    }
  }


  initializeMaterial() {
    this.currentlySavedImage = this.materialBeingUpdated?.defaultImage ?? null;

    // UI-only state.
    this.uploadValue = '1';
    this.defaultImageNumber = 1;

    this.updatedMaterialInfo = new MediaFile();
    this.updatedMaterialInfo.title = this.materialBeingUpdated?.title ?? '';
    this.updatedMaterialInfo.categories = this.materialBeingUpdated?.categories ?? [];
    this.updatedMaterialInfo.description = this.materialBeingUpdated?.description ?? '';
    this.updatedMaterialInfo.fileType = this.materialBeingUpdated?.fileType ?? '';

    if (this.materialBeingUpdated?.categories?.length) this.getInitCategoriesForArray();

    this.updateDescriptionWordStats(); // Set description word counts.

    this.showLoader = false;
    this.checkFormValidation();
  }


  getMaterialToEdit(_skipCached: boolean = false): Promise<MediaFile | null> {
    return new Promise(async (resolve) => {
      try {
        let materialToEdit = (!_skipCached) ? this._materialService?.getMaterialToEdit() : null;

        if (!materialToEdit?._id && this.currentMaterialId) {
          const foundMaterial = await lastValueFrom(this._materialService.getMediaById(this.currentMaterialId));
          materialToEdit = foundMaterial?._id ? foundMaterial : null;
        }

        // Clone if found so one from service is not modified.
        if (materialToEdit?._id) materialToEdit = this._sharedService.deepClone(materialToEdit);

        resolve(materialToEdit);
      } catch (_err) {
        console.log('Error retrieving material to edit: ', _err?.message ?? _err);
        resolve(null);
      }
    });
  }


  getMaterialCategories() {
    return new Promise(async (resolve) => {
      try {
        const foundCategories = await lastValueFrom(this._materialCategoryService.getMaterialCategories());
        this.materialCategories = foundCategories?.length ? foundCategories : [];

        resolve(null);
      } catch (_err) {
        console.log('Error retrieving material categories: ', _err?.message ?? _err);
        this.materialCategories = [];
        resolve(null);
      }
    });
  }


  getInitCategoriesForArray() {
    this.currentCategories = [];

    this.materialBeingUpdated.categories.forEach(_category => {
      const matchedCategory = this.materialCategories.find(_cat => _cat?.name === _category?.name);

      this.currentCategories.push({
        category: _category,
        subCategory: _category.subCategory ?? null,
        availableSubCategories: matchedCategory?.subCategories ?? []
      });
    });
  }


  onDescriptionKeydown(_event: KeyboardEvent) {
    // Event fired from the description textarea on keydown.
    this.updateDescriptionWordStats();

    if (this.maxLimitReached && _event?.key !== 'Backspace') _event.preventDefault();
  }


  updateDescriptionWordStats() {
    // Sets word counts for description textarea.
    const value = this.updatedMaterialInfo?.description || '';
    const words = value.trim().length ? value.match(/\S+/g)?.length || 0 : 0;

    this.currentWordCount = words;
    this.wordsLeft = Math.max(this.descriptionLimit - words, 0);
    this.maxLimitReached = (words >= this.descriptionLimit);
  }


  addCategory(): void {
    this.currentCategories.push({ category: null, subCategory: null, availableSubCategories: [] });
    this.checkInformation();
  }


  removeGroup(cat) {
    const index: number = this.currentCategories.indexOf(cat);

    if (index !== -1) {
      this.currentCategories.splice(index, 1);
    }

    this.checkInformation();
  }


  checkForSubs(categoryToFilterOn, index) {
    this.currentSubCategories = [];
    this.currentCategories[index].availableSubCategories = [];
    this.currentCategories[index].subCategory = null;

    if (this.currentCategories[index]?.category && categoryToFilterOn?.subCategories?.length) {
      this.currentCategories[index].availableSubCategories = categoryToFilterOn.subCategories;
    }

    this.checkInformation();
  }


  subCatChanged(subCategoryChosen, index) {
    this.currentCategories[index].subCategory = subCategoryChosen;
    this.checkInformation();
  }


  checkInformation() {
    this.hasError = { messages: [] };
    let passesInfoCheck = true;

    if (this.currentCategories === null || !this.currentCategories || !this.currentCategories.length) {
      passesInfoCheck = false;
      this.hasError.messages.push('Materials must have at least one category');
    }

    if (this.currentCategories && this.currentCategories.length) {
      let hasMissingCat = false;
      let hasMissingCatWithSubCats = false;

      this.currentCategories.forEach(_category => {
        if (_category.category === undefined || _category.category === null) {
          this.hasError.messages.push('You must choose a category, or remove the group');
          hasMissingCat = true;
        }
        if (_category.availableSubCategories && _category.availableSubCategories.length && (!_category.subCategory || _category.subCategory === null)) {
          this.hasError.messages.push('Sub Category missing for ' + _category.category.name);
          hasMissingCatWithSubCats = true;
        }
      });

      if (hasMissingCat || hasMissingCatWithSubCats) { passesInfoCheck = false; }
    }

    if (!this.hasError.messages.length) {
      if (this.currentCategories && this.currentCategories.length && this.currentCategories[0].category) {
        this.updatedMaterialInfo.categories = this.currentCategories.map(_cc => {
          return { name: _cc.category.name, subCategory: _cc.subCategory };
        });
      } else {
        passesInfoCheck = false;
      }
    }

    this.categoryMissing = !passesInfoCheck;
    this.subCategoryMissing = !passesInfoCheck;
    this.checkFormValidation();
  }


  currentPreviewImageSelected(_image) {
    // console.log('Selected Current Preview Image: ', _image);
    if (_image) this.currentlySavedImage = _image;
  }


  toggleChangeContent() {
    this.changeContent = !this.changeContent;

    // Reset changed file content and reset values so not used in update.
    if (!this.changeContent) this.clearPendingFileChanges();

    this.checkFormValidation();
  }


  addFiles() {
    this.file.nativeElement.click();
  }


  clearPendingFileChanges() {
    this.updatedMaterialInfo.fileType = this.materialBeingUpdated?.fileType ?? ''; // Reset fileType to original
    this.resetArraysAndValues(); // Reset other file related stuff.
  }


  resetArraysAndValues() {
    // Only reset these when selecting a NEW main file.
    this.fileToPreview = null;
    this.coverImages = [];
    this.tempMainFileName = null;
    this.uploadValue = '1';
    this.showLoader = false;

    this.files.clear();
    this.files2.clear();

    // Reset paths on material info.
    this.updatedMaterialInfo.defaultImage = '';
    this.updatedMaterialInfo.fileImageArray = [];
    this.updatedMaterialInfo.pdfPath = '';
    this.updatedMaterialInfo.videoPath = '';

    this.clearSelections();
  }


  clearSelections() {
    // Clears cover image selections.
    this.selectedCount = 0;
    this.imagesToUpload = [];
  }


  /* A file was selected from drag n drop box */
  async onFilesAdded(_event: any) {
    this.resetArraysAndValues();

    this.showLoader = true; // Start loader for any file processing.

    try {
      let fileUploaded: File;
      const files: { [key: string]: File } = this.file?.nativeElement?.files ?? [];

      // If no files, reset default image and return early.
      if (!files?.length) {
        this.updatedMaterialInfo.defaultImage = '';
        return;
      }

      for (const key in files) {
        if (!isNaN(parseInt(key, 0))) {
          fileUploaded = files[key];
          this.files.add(fileUploaded);
          break;
        }
      }

      if (!fileUploaded) return;

      this.tempMainFileName = fileUploaded?.name;
      const extension = this.tempMainFileName.split('.').pop()?.toLowerCase();

      // If not an allowed file type reset values, show message, and return early.
      if (this.filetypesToNotInclude.includes(extension)) {
        this.resetArraysAndValues();
        this._sharedService.showErrorDialog('File type not accepted. Please upload an image, PDF, or video.');
        return;
      }

      // Handle file types.
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
        this.updatedMaterialInfo.fileType = 'Image';
        await this.handleImagePreview(fileUploaded);
      } else if (extension === 'pdf') {
        this.updatedMaterialInfo.fileType = 'PDF';
        await this.handlePdfPreview(fileUploaded);
      } else {
        this.updatedMaterialInfo.fileType = 'Video';
        await this.handleVideoPreview(fileUploaded);
      }
    } catch (_err) {
      console.log('Error handling uploaded file: ', _err);
    }

    this.checkFormValidation();
    this.showLoader = false; // Stop loader after all processing.
  }


  async handleImagePreview(_file: File) {
    if (!_file) return;

    const fileDataUrl = await this._fileService.readFileAsDataUrl(_file);

    if (!fileDataUrl?.length) {
      console.log('No preview data for file: ', _file?.name);
      return; // Exit early if file couldn't be read.
    }

    this.fileToPreview = fileDataUrl;
    this.coverImages = [this.fileToPreview];

    this.updatedMaterialInfo.defaultImage = this.tempMainFileName;
    this.updatedMaterialInfo.fileImageArray = [];
  }


  async handlePdfPreview(_file: File) {
    if (!_file) return; // If no file return early.

    // Store PDF path. Don't need FileReader here since previews come from generated images.
    this.updatedMaterialInfo.pdfPath = this.tempMainFileName;

    try {
      // Generate image files from PDF.
      const imageFiles: File[] = await this._fileService.generatePdfImageArray(_file);
      // console.log('imageFiles: ', imageFiles);

      if (!imageFiles?.length) return;

      this.originalCoverImages = imageFiles;
      this.imagesToUpload = [...imageFiles];

      this.files2.clear(); // Clear file set.

      for (const _imageFile of imageFiles) {
        this.files2.add(_imageFile); // Real file for upload.
      }

      this.updateCoverImagesPreview();

      // Wire material data.
      this.updatedMaterialInfo.defaultImage = imageFiles[0]?.name ?? '';
      this.updatedMaterialInfo.fileImageArray = imageFiles.map(_f => _f?.name);
    } catch (_err) {
      console.log('Error handling PDF preview: ', _err);
    }
  }


  async handleVideoPreview(_file: File) {
    if (!_file) return;

    const fileDataUrl = await this._fileService.readFileAsDataUrl(_file);

    if (!fileDataUrl?.length) {
      console.log('No preview data for file: ', _file?.name);
      return; // Exit early if file couldn't be read
    }

    this.fileToPreview = fileDataUrl;

    // Store video path.
    this.updatedMaterialInfo.videoPath = this.tempMainFileName;

    try {
      const thumbnailFile = await this._fileService.generateVideoThumbnail(_file); // Generate thumbnail file.
      // console.log('thumbnailFile: ', thumbnailFile);

      if (thumbnailFile) {
        this.originalCoverImages = [thumbnailFile];
        this.imagesToUpload = [thumbnailFile];

        this.files2.clear();
        this.files2.add(thumbnailFile); // Push real generated image file to second file array to upload.

        this.updateCoverImagesPreview();

        // Wire material data
        this.updatedMaterialInfo.defaultImage = thumbnailFile?.name ?? '';
        this.updatedMaterialInfo.fileImageArray = [thumbnailFile?.name];
      }
    } catch (_err) {
      console.log('Error generating video thumbnail: ', _err);
    }
  }


  updateCoverImagesPreview() {
    this.coverImages.forEach(_url => {
      if (_url?.startsWith('blob:')) URL.revokeObjectURL(_url);
    });

    const imagesToUpload = this.imagesToUpload?.length ? this.imagesToUpload : [];
    this.coverImages = imagesToUpload.map(_f => URL.createObjectURL(_f));
    // console.log('Cover Images: ', this.coverImages);

    // Update preview file if these types.
    if (['Image', 'PDF'].includes(this.updatedMaterialInfo?.fileType)) this.fileToPreview = this.coverImages[0] ?? null;
  }


  onOwnCoverImageUpload(event: any) {
    const files: FileList = this.ownCoverImage.nativeElement.files;

    if (!files || files.length === 0) {
      this.uploadValue = '1'; // restore option 1.
      this.checkUploadThumbnailValue();
      return;
    }

    this.imagesToUpload = [];
    this.files2.clear();

    Array.from(files).forEach(file => {
      this.imagesToUpload.push(file);
      this.files2.add(file);
    });

    this.updateCoverImagesPreview();

    // Wire material data.
    this.updatedMaterialInfo.defaultImage = this.imagesToUpload[0]?.name ?? '';
    this.updatedMaterialInfo.fileImageArray = this.imagesToUpload.map(_f => _f?.name);
  }


  checkUploadThumbnailValue() {
    // Set original values from main upload back.
    if (this.uploadValue === '1') {
      this.stopRepeatFireOfEvent = false;
      this.imagesToUpload = [...this.originalCoverImages];
      this.files2.clear();
      this.imagesToUpload.forEach(f => this.files2.add(f));
      this.updateCoverImagesPreview();

      // Wire material data.
      this.updatedMaterialInfo.defaultImage = this.imagesToUpload[0]?.name ?? '';
      this.updatedMaterialInfo.fileImageArray = this.imagesToUpload.map(_f => _f?.name);
    }

    // User will upload their own cover images.
    if (this.uploadValue === '2' && !this.stopRepeatFireOfEvent) {
      this.stopRepeatFireOfEvent = true;
      this.imagesToUpload = [];
      this.files2.clear();
      this.coverImages = [];
    }

    // console.log('this.files2: ', this.files2);
    // console.log('imagesToUpload: ', this.imagesToUpload);
  }


  imageSelected(_image) {
    // console.log('Selected Image: ', _image);

    if (this.updatedMaterialInfo.fileType !== 'Video') {
      this.fileToPreview = _image;
    }
  }


  showTermsDialog() {
    // Caller is commented out in view. If we add back then add a dialog to use here.   
  }


  async updateMaterial() {
    if (!this.validForm) {
      this._sharedService.showErrorDialog('Form invalid. Please make sure everything is filled out and try again.', 6000);
      return;
    }

    this.showLoader = true;

    // Only upload files if the user actually changed content.
    if (this.tempMainFileName || this.files2?.size) {
      const uploadsOk = await this.processFiles();
      if (!uploadsOk) {
        this._sharedService.showErrorDialog('Error uploading material files.', 6000);
        this.showLoader = false;
        return;
      }
    }

    // Set updated data to material to send for edit.
    const updated = this.updatedMaterialInfo;
    const material = this.materialBeingUpdated;
    // console.log('Form data: ', updated);
    // console.log('Material to update: ', material);

    material.title = updated.title;
    material.description = updated.description;
    material.categories = updated.categories;
    material.fileType = updated.fileType;

    if (updated.defaultImage?.length) material.defaultImage = updated.defaultImage;

    if (updated.pdfPath?.length) material.filePath = updated.pdfPath;
    else if (updated.videoPath?.length) material.filePath = updated.videoPath;

    if (updated.fileImageArray?.length) material.fileImageArray = updated.fileImageArray;

    // Try to perform the update and handle it.
    try {
      const _updatedMaterial = await lastValueFrom(this._materialService.update(material));
      if (_updatedMaterial?._id) {
        this.materialStore.push(_updatedMaterial);
        this.materialBeingUpdated = _updatedMaterial;
        this._materialService.setMaterialToEdit(null); // Clear material set since edit is over.
        this._materialService.setCurrentMaterialBeingViewed(this.materialBeingUpdated); // Set so profile can fetch it if going back to it.
        this.showLoader = false;
        this.goBack();
      } else {
        this._sharedService.showErrorDialog('Error saving material. Save Cancelled.', 6000);
        this.showLoader = false; // Stop loader failed save.
      }
    } catch (err) {
      console.error('Error creating material:', err);
      this._sharedService.showErrorDialog('Error saving material. Save Cancelled.', 6000);
      this.showLoader = false; // Stop loader due to error.
    }
  }


  async processFiles(): Promise<boolean> {
    try {
      // Upload main file if provided
      if (this.files?.size) {
        const mainFile = Array.from(this.files)[0];
        const mainLink = await this.uploadFileAndGetLink(mainFile);

        if (!mainLink?.length) return false;

        if (this.updatedMaterialInfo.fileType === 'PDF') {
          this.updatedMaterialInfo.pdfPath = mainLink;
        } else if (this.updatedMaterialInfo.fileType === 'Video') {
          this.updatedMaterialInfo.videoPath = mainLink;
        } else if (this.updatedMaterialInfo.fileType === 'Image') {
          this.updatedMaterialInfo.defaultImage = mainLink;
          this.updatedMaterialInfo.fileImageArray = [mainLink];
        }
      }

      // Upload cover images (non-image types only)
      if (this.files2?.size && this.updatedMaterialInfo.fileType !== 'Image') {
        const imageLinks: string[] = [];

        for (const file of this.files2) {
          const link = await this.uploadFileAndGetLink(file);
          if (link?.length) imageLinks.push(link);
        }

        if (!imageLinks.length) return false;

        this.updatedMaterialInfo.fileImageArray = imageLinks;
        this.updatedMaterialInfo.defaultImage = imageLinks[0];
      }

      return true;
    } catch (err) {
      console.error('processFilesForEdit error:', err);
      return false;
    }
  }


  uploadFileAndGetLink(_file: File): Promise<string | null> {
    return new Promise(async (resolve) => {
      const pathSpecified = 'materials';

      if (_file) {
        try {
          const uploadResult = await lastValueFrom(this._fileService.uploadToSpecifedFolder(new Set([_file]), pathSpecified));
          const resultsPart: any = (uploadResult.length) ? uploadResult[0] : uploadResult;
          const linkToReturn = resultsPart?.body?.link ?? null;

          resolve(linkToReturn);
        } catch (_err) {
          console.error(`Error uploading image ${_file?.name}`, _err);
          resolve(null);
        }
      } else {
        console.error('No file to upload. Skipping upload.');
        resolve(null);
      }
    });
  }


  checkFormValidation() {
    const materialInfo = this.updatedMaterialInfo;
    let passesChecks = true;

    if (
      !materialInfo?.title?.length ||
      !materialInfo?.description?.length ||
      !materialInfo?.categories?.length ||
      this.categoryMissing ||
      this.subCategoryMissing ||
      (this.changeContent && !materialInfo?.defaultImage?.length)
    ) {
      passesChecks = false;
    }

    this.validForm = passesChecks;
  }


  dropdownNameCompareFn(c1: Object, c2: Object): boolean {
    return c1 && c2 ? c1['name'] === c2['name'] : c1 === c2;
  }


  goBack(): void {
    this._location.back();
  }
}